import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addSelectedReport } from '../../../redux/userReducer';
import {
  STATUS_COLOR,
  STATUS_LABEL,
  useReportPreloadRows,
} from '../../Header/ReportPreloadStatus';
import classes from './SideBarComponent.module.css';

const SideBarComponent = (props) => {
  const selectedReportGlobal = useSelector(
    (state) => state.PowerBi.selectedReport,
  );
  const { reports, handleReports, loading = false, loadingLabel, emptyLabel } = props;
  const dispatch = useDispatch();
  const { statusFor } = useReportPreloadRows();
  // The loading / ready dots are for admins only.
  const isAdmin = Boolean(useSelector((state) => state.PowerBi.user?.ISADMIN));

  // Dot beside a report: orange while it loads in the background, green once
  // ready; no dot otherwise.
  const renderStatusDot = (report, sectionName) => {
    if (!isAdmin) return null;
    // Only reports that are loaded (green) or being loaded (orange) get a
    // dot; everything else (waiting, loads when opened, failed) has none.
    const status = statusFor(report.PBIREPORTID);
    const isReady = status === 'ready' || status === 'slow';
    if (status !== 'loading' && !isReady) return null;
    const color = isReady ? STATUS_COLOR.ready : STATUS_COLOR.loading;
    return (
      <Tooltip
        arrow
        placement='right'
        title={
          <div style={{ display: 'grid', gap: 2 }}>
            <div style={{ fontWeight: 600 }}>{report.ReportName}</div>
            <div style={{ opacity: 0.85 }}>Section: {sectionName}</div>
            <div>{STATUS_LABEL[status]}</div>
          </div>
        }
      >
        <span
          aria-label={`${report.ReportName}: ${STATUS_LABEL[status]}`}
          className={`${classes.statusDot} ${
            status === 'loading' ? classes.statusDotLoading : ''
          }`}
          style={{ background: color }}
        />
      </Tooltip>
    );
  };

  const [selectedreport, setSelectedReport] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [open, setOpen] = useState(null);

  const handleClick = (report) => {
    setSelectedReport(report?.PBIREPORTID);
    setSelectedGroup(report?.NAME);
    dispatch(addSelectedReport(report));
    handleReports(report);
  };

  useEffect(() => {
    if (Object.keys(selectedReportGlobal).length !== 0) {
      setSelectedReport(selectedReportGlobal?.PBIREPORTID);
      setSelectedGroup(selectedReportGlobal?.NAME);
    }
  }, [selectedReportGlobal]);

  return (
    <div className={classes.sidebarContainer}>
      <div className={classes.customScroll}>
        {loading && reports.length === 0 && (
          <Box sx={{ p: 2 }}>
            {loadingLabel && (
              <Typography
                sx={{
                  color: '#f7901d',
                  fontSize: 13,
                  mb: 1.5,
                  wordBreak: 'break-all',
                }}
              >
                {loadingLabel}
              </Typography>
            )}
            {[...Array(6)].map((_, i) => (
              <Skeleton
                key={i}
                variant='text'
                height={28}
                sx={{ bgcolor: 'rgba(255,255,255,0.12)' }}
              />
            ))}
          </Box>
        )}
        {emptyLabel && reports.length === 0 && !loading && (
          <Typography sx={{ color: '#f7901d', fontSize: 13, p: 2 }}>
            {emptyLabel}
          </Typography>
        )}
        {reports.length > 0 &&
          reports.map((arr, indexTop) => (
            <div
              key={indexTop}
              className={`${classes.groupSection} ${
                reports.length - 1 === indexTop ? classes.lastSection : ''
              }`}
            >
              {/* Group Title Header */}
              <div
                className={`${classes.groupHeader} ${
                  selectedGroup === arr[0] ? classes.selectedHeader : ''
                }`}
              >
                <Typography className={classes.titleText}>{arr[0]}</Typography>
              </div>

              {/* Reports Navigation Links */}
              <div className={classes.reportsContainer}>
                {arr[1].slice(0, 3).map((report) => (
                  <div
                    key={report.PBIREPORTID}
                    className={
                      selectedreport === report.PBIREPORTID
                        ? classes.selectedReport
                        : classes.report
                    }
                    onClick={() => handleClick(report)}
                  >
                    <span className={classes.reportfontStyle}>
                      {report.ReportName}
                    </span>
                    {renderStatusDot(report, arr[0])}
                  </div>
                ))}

                {/* See More / Less Toggle */}
                {arr[1].length > 3 && (
                  <Box className={classes.extraReports}>
                    <span className={classes.extraReportText}>
                      {arr[0] === open ? (
                        <span
                          onClick={() => setOpen(null)}
                          style={{ cursor: 'pointer' }}
                        >
                          less
                        </span>
                      ) : (
                        <span
                          onClick={() => setOpen(arr[0])}
                          style={{ cursor: 'pointer' }}
                        >
                          {`${arr[1].length - 3} more`}
                        </span>
                      )}
                    </span>
                  </Box>
                )}

                {/* Expanded Nav Items */}
                {arr[0] === open &&
                  arr[1].slice(3).map((report) => (
                    <div
                      key={report.PBIREPORTID}
                      className={
                        selectedreport === report.PBIREPORTID
                          ? classes.selectedReport
                          : classes.report
                      }
                      onClick={() => handleClick(report)}
                    >
                      <span className={classes.reportfontStyle}>
                        {report.ReportName}
                      </span>
                      {renderStatusDot(report, arr[0])}
                    </div>
                  ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default SideBarComponent;
